import { type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Section } from "../components/ui/section";
import { PageHeader } from "../components/ui/page-header";
import { Skeleton } from "../components/ui/skeleton";
import { Card, CardContent } from "../components/ui/card";
import { SettlementForm } from "../components/SettlementForm";
import { useCreateSettlement, useGroup } from "../api/hooks";

/**
 * Record a settlement (a payment from one member to another). Optional
 * `?from=&to=&amount=` query params pre-fill the form from a "settle up"
 * suggestion so a single tap on the balances breakdown lands here ready to
 * confirm.
 */
export function CreateSettlementPage(): ReactNode {
  const { groupId } = useParams<{ groupId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const groupQ = useGroup(groupId);
  const createSettlement = useCreateSettlement(groupId ?? "");

  // Back to the Settle up screen: recording a payment changes what's still
  // owed, which is what that screen shows (issue #453).
  const groupPath = `/groups/${groupId}/settle`;

  if (groupQ.isLoading) {
    return (
      <div className="mx-auto max-w-xl space-y-stack-lg">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!groupQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Group not found.
        </CardContent>
      </Card>
    );
  }

  const group = groupQ.data;
  const fromParam = searchParams.get("from") ?? undefined;
  const toParam = searchParams.get("to") ?? undefined;
  const amountParam = searchParams.get("amount");
  const defaultAmountMinor =
    amountParam !== null && /^\d+$/.test(amountParam)
      ? Number(amountParam)
      : undefined;

  // Only honour pre-fill ids that actually belong to the group's roster.
  const ids = new Set(group.participants.map((p) => p.id));
  const defaultPayerId =
    fromParam && ids.has(fromParam) ? fromParam : undefined;
  const defaultPayeeId = toParam && ids.has(toParam) ? toParam : undefined;

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader
        back={{ label: "Back", onClick: () => navigate(groupPath) }}
        title="Record a payment"
        description={`Settle up in ${group.label}. This records that one person paid another.`}
      />
      <Section
        title="Payment"
        description="Pick who paid whom and how much. Amounts are in the group currency."
      >
        <SettlementForm
          group={group}
          defaultPayerId={defaultPayerId}
          defaultPayeeId={defaultPayeeId}
          defaultAmountMinor={defaultAmountMinor}
          submitting={createSettlement.isPending}
          serverError={
            createSettlement.error
              ? "Couldn't record the payment. Please check the details and try again."
              : null
          }
          onSubmit={async (values) => {
            await createSettlement.mutateAsync(values);
            navigate(groupPath);
          }}
          onCancel={() => navigate(groupPath)}
        />
      </Section>
    </div>
  );
}
